import React, { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "bootstrap-icons/font/bootstrap-icons.css";
import logo from "../assets/logohorizental.png";
import NotificationBell from "../components/NotificationBell";

const API = "/api";

const pageStyle = {
  height: "100vh",
  background: "radial-gradient(circle at top left, #dbeafe 0%, #f8fafc 34%, #fff7ed 100%)",
  padding: 0,
  fontFamily: "'Segoe UI', sans-serif",
  overflow: "hidden",
};

const appShellStyle = {
  width: "100%",
  height: "100vh",
  display: "grid",
  gridTemplateColumns: "248px minmax(0, 1fr)",
  gap: "0",
  alignItems: "stretch",
  overflow: "hidden",
};

const sidebarStyle = {
  background: "linear-gradient(180deg, #dbeafe 0%, #f8fafc 38%, #fff7ed 100%)",
  color: "#4e1b24",
  padding: "20px 16px 18px",
  boxShadow: "18px 0 45px rgba(148, 163, 184, 0.18)",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  borderRadius: "0",
  borderRight: "1px solid rgba(148, 163, 184, 0.18)",
};

const mainPanelStyle = {
  display: "grid",
  gridTemplateRows: "auto minmax(0, 1fr)",
  minHeight: "100vh",
  height: "100vh",
  overflow: "hidden",
};

const topBarShellStyle = {
  background: "linear-gradient(120deg, rgba(219,234,254,0.96) 0%, rgba(248,250,252,0.97) 44%, rgba(255,237,213,0.98) 100%)",
  color: "#4e1b24",
  padding: "14px 20px",
  boxShadow: "0 18px 40px rgba(148, 163, 184, 0.12)",
  borderRadius: "0",
  borderBottom: "1px solid rgba(148, 163, 184, 0.18)",
  backdropFilter: "blur(12px)",
  position: "relative",
  zIndex: 60,
};

const topBarStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "18px",
  flexWrap: "wrap",
};

const contentWrapStyle = {
  padding: "24px",
  overflowY: "auto",
  overflowX: "hidden",
  minHeight: 0,
};

const contentCardStyle = {
  background: "rgba(255,255,255,0.94)",
  borderRadius: "24px",
  padding: "24px",
  border: "1px solid rgba(148, 163, 184, 0.18)",
  boxShadow: "0 18px 38px rgba(15, 23, 42, 0.08)",
};

const primaryButtonStyle = {
  border: "none",
  background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
  color: "#fff",
  borderRadius: "999px",
  width: "46px",
  height: "46px",
  padding: "0",
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 14px 24px rgba(124, 45, 58, 0.24)",
};

const avatarButtonStyle = {
  width: "48px",
  height: "48px",
  borderRadius: "999px",
  border: "1px solid rgba(148, 163, 184, 0.18)",
  background: "rgba(255,255,255,0.72)",
  color: "#7c2d3a",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800,
  fontSize: "16px",
  cursor: "pointer",
};

const profileMenuStyle = {
  position: "fixed",
  width: "300px",
  background: "#ffffff",
  borderRadius: "22px",
  boxShadow: "0 22px 50px rgba(15, 23, 42, 0.18)",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  padding: "18px",
  zIndex: 120,
  boxSizing: "border-box",
};

const franchiseHeroStyle = {
  background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 50%, #c2410c 100%)",
  color: "#fff",
  borderRadius: "28px",
  padding: "28px",
  boxShadow: "0 30px 60px rgba(78, 27, 36, 0.24)",
  position: "relative",
  overflow: "hidden",
  marginBottom: "18px",
};

const franchiseHeroGlowStyle = {
  position: "absolute",
  inset: "auto -60px -90px auto",
  width: "230px",
  height: "230px",
  background: "radial-gradient(circle, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 70%)",
};

const franchiseHeroSideCardStyle = {
  background: "rgba(255,255,255,0.14)",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: "18px",
  padding: "14px 16px",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
};

const profileActionStyle = {
  width: "100%",
  textAlign: "left",
  border: "none",
  background: "#f8fafc",
  color: "#0f172a",
  borderRadius: "14px",
  padding: "12px 14px",
  fontWeight: 600,
  marginTop: "8px",
};

const themedSelectStyle = {
  borderRadius: "16px",
  border: "1px solid rgba(124, 45, 58, 0.18)",
  background: "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(255,247,237,0.98) 100%)",
  color: "#4e1b24",
  padding: "10px 14px",
  fontWeight: 700,
  boxShadow: "0 12px 24px rgba(124, 45, 58, 0.10)",
  transition: "all 0.2s ease",
  cursor: "pointer",
};

const customDropdownMenuStyle = {
  position: "absolute",
  top: "calc(100% + 8px)",
  left: 0,
  right: 0,
  borderRadius: "18px",
  border: "1px solid rgba(255,255,255,0.18)",
  background: "linear-gradient(180deg, rgba(255,255,255,0.26) 0%, rgba(255,247,237,0.18) 100%)",
  backdropFilter: "blur(18px)",
  boxShadow: "0 20px 40px rgba(78, 27, 36, 0.24)",
  padding: "8px",
  zIndex: 80,
};

function SessionDropdown({ value, options, onChange, variant = "light" }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleOutside = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const buttonStyle = variant === "hero"
    ? {
        width: "100%",
        borderRadius: "16px",
        border: hovered || open ? "1px solid rgba(255,255,255,0.48)" : "1px solid rgba(255,255,255,0.18)",
        background: "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.12) 100%)",
        color: "#ffffff",
        padding: "10px 14px",
        fontWeight: 700,
        boxShadow: hovered || open ? "0 18px 30px rgba(78, 27, 36, 0.24)" : "0 12px 24px rgba(78, 27, 36, 0.16)",
        transition: "all 0.2s ease",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
      }
    : {
        ...themedSelectStyle,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        border: hovered || open ? "1px solid rgba(194, 65, 12, 0.48)" : themedSelectStyle.border,
        boxShadow: hovered || open ? "0 16px 28px rgba(124, 45, 58, 0.16)" : themedSelectStyle.boxShadow,
      };

  const menuStyle = variant === "hero"
    ? customDropdownMenuStyle
    : {
        ...customDropdownMenuStyle,
        border: "1px solid rgba(124, 45, 58, 0.14)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(255,247,237,0.54) 100%)",
        boxShadow: "0 20px 40px rgba(124, 45, 58, 0.16)",
      };

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        style={buttonStyle}
        onClick={() => setOpen((prev) => !prev)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-expanded={open}
      >
        <span>{value || "Select Session"}</span>
        <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
      </button>

      {open ? (
        <div style={menuStyle}>
          {(options || []).map((option) => {
            const selected = option === value;
            return (
              <button
                key={option}
                type="button"
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius: "14px",
                  padding: "10px 12px",
                  background: selected
                    ? (variant === "hero" ? "rgba(255,255,255,0.18)" : "linear-gradient(135deg, rgba(124,45,58,0.12) 0%, rgba(194,65,12,0.12) 100%)")
                    : "transparent",
                  color: variant === "hero" ? "#ffffff" : "#4e1b24",
                  fontWeight: selected ? 800 : 700,
                  textAlign: "left",
                  transition: "all 0.18s ease",
                  cursor: "pointer",
                  marginBottom: "4px",
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = variant === "hero"
                    ? "rgba(255,255,255,0.14)"
                    : "linear-gradient(135deg, rgba(124,45,58,0.12) 0%, rgba(194,65,12,0.12) 100%)";
                  event.currentTarget.style.transform = "translateX(3px)";
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = selected
                    ? (variant === "hero" ? "rgba(255,255,255,0.18)" : "linear-gradient(135deg, rgba(124,45,58,0.12) 0%, rgba(194,65,12,0.12) 100%)")
                    : "transparent";
                  event.currentTarget.style.transform = "translateX(0)";
                }}
              >
                {option}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function easeOutCubic(progress) {
  return 1 - Math.pow(1 - progress, 3);
}

function useAnimatedNumber(value, duration = 1200) {
  const [animatedValue, setAnimatedValue] = useState(Number(value || 0));
  const previousValueRef = useRef(Number(value || 0));

  useEffect(() => {
    const nextValue = Number(value || 0);
    const startValue = previousValueRef.current;

    if (Math.abs(nextValue - startValue) < 0.01) {
      setAnimatedValue(nextValue);
      previousValueRef.current = nextValue;
      return undefined;
    }

    let frameId = null;
    let startTime = null;

    const animate = (timestamp) => {
      if (startTime === null) {
        startTime = timestamp;
      }

      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      const currentValue = startValue + (nextValue - startValue) * easedProgress;

      setAnimatedValue(currentValue);

      if (progress < 1) {
        frameId = window.requestAnimationFrame(animate);
      } else {
        previousValueRef.current = nextValue;
      }
    };

    frameId = window.requestAnimationFrame(animate);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [duration, value]);

  return animatedValue;
}

function FranchiseDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const franchise = JSON.parse(localStorage.getItem("franchise") || "null");
  const [summary, setSummary] = useState({
    wallet_balance: 0,
    tools_point_balance: 0,
    gift_card_point_balance: 0,
    skills_point_balance: 0,
    total_point_balance: 0,
    wallets: [
      { key: "main_wallet", label: "Wallet Balance", balance: 0 },
      { key: "tools_wallet", label: "Tools Point", balance: 0 },
      { key: "gift_card_wallet", label: "Gift Card", balance: 0 },
      { key: "reserve_wallet", label: "Skill Point", balance: 0 },
    ],
  });
  const [activeSession, setActiveSession] = useState(() => localStorage.getItem("activeAcademicSession") || "Not Set");
  const [availableSessions, setAvailableSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(() => localStorage.getItem("franchiseSelectedAcademicSession") || localStorage.getItem("activeAcademicSession") || "Not Set");
  const [showSidebarMenu, setShowSidebarMenu] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState("");
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window === "undefined" ? 1440 : window.innerWidth));
  const profileMenuRef = useRef(null);
  const profileButtonRef = useRef(null);
  const [profileMenuPosition, setProfileMenuPosition] = useState({ top: 72, right: 12, width: 300 });
  const animatedWalletBalance = useAnimatedNumber(summary.wallet_balance);

  useEffect(() => {
    if (!franchise?.id) return;
    let active = true;

    axios.get(`${API}/franchise-portal/${franchise.id}/profile`)
      .then((res) => {
        if (active && res.data?.photo) {
          setProfilePhoto(`/uploads/${res.data.photo}`);
        }
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [franchise?.id]);

  useEffect(() => {
    if (!franchise?.id) return;

    let active = true;

    const loadSummary = async () => {
      try {
        const res = await axios.get(`${API}/franchise-portal/${franchise.id}/summary`);
        if (!active) {
          return;
        }
        setSummary(
            res.data || {
              wallet_balance: 0,
              tools_point_balance: 0,
              gift_card_point_balance: 0,
              skills_point_balance: 0,
              total_point_balance: 0,
              wallets: [
                { key: "main_wallet", label: "Wallet Balance", balance: 0 },
                { key: "tools_wallet", label: "Tools Point", balance: 0 },
                { key: "gift_card_wallet", label: "Gift Card", balance: 0 },
                { key: "reserve_wallet", label: "Skill Point", balance: 0 },
              ],
          }
        );
      } catch {
        if (!active) {
          return;
        }
        setSummary({
          wallet_balance: 0,
          tools_point_balance: 0,
          gift_card_point_balance: 0,
          skills_point_balance: 0,
          total_point_balance: 0,
          wallets: [
            { key: "main_wallet", label: "Wallet Balance", balance: 0 },
            { key: "tools_wallet", label: "Tools Point", balance: 0 },
            { key: "gift_card_wallet", label: "Gift Card", balance: 0 },
            { key: "reserve_wallet", label: "Skill Point", balance: 0 },
          ],
        });
      }
    };

    loadSummary();
    const summaryInterval = window.setInterval(loadSummary, 10000);

    return () => {
      active = false;
      window.clearInterval(summaryInterval);
    };
  }, [franchise?.id]);

  useEffect(() => {
    let active = true;

    const loadAcademicSessions = async () => {
      try {
        const res = await axios.get(`${API}/academic-sessions`, {
          params: {
            _ts: Date.now(),
          },
        });
        if (!active) return;
        const nextSession = res.data?.active_session || localStorage.getItem("activeAcademicSession") || "Not Set";
        const sessionRows = Array.isArray(res.data?.sessions) ? res.data.sessions : [];
        const sessionNames = sessionRows.map((item) => item.session_name).filter(Boolean);
        setActiveSession(nextSession);
        setAvailableSessions(sessionNames);
        localStorage.setItem("activeAcademicSession", nextSession);
        setSelectedSession((previous) => {
          const resolvedSession = previous && sessionNames.includes(previous) ? previous : nextSession;
          localStorage.setItem("franchiseSelectedAcademicSession", resolvedSession);
          return resolvedSession;
        });
      } catch {
        if (!active) return;
        setActiveSession(localStorage.getItem("activeAcademicSession") || "Not Set");
        setAvailableSessions([]);
      }
    };

    loadAcademicSessions();

    const handleSessionChange = () => {
      loadAcademicSessions();
    };

    const handleStorageChange = (event) => {
      if (event.key === "activeAcademicSession") {
        loadAcademicSessions();
      }
    };

    window.addEventListener("academic-session-changed", handleSessionChange);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      active = false;
      window.removeEventListener("academic-session-changed", handleSessionChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("franchiseSelectedAcademicSession", selectedSession || activeSession || "Not Set");
    window.dispatchEvent(new Event("franchise-session-changed"));
  }, [activeSession, selectedSession]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!showProfileMenu) {
      return undefined;
    }

    const updateProfileMenuPosition = () => {
      if (!profileButtonRef.current) {
        return;
      }

      const rect = profileButtonRef.current.getBoundingClientRect();
      const mobileViewport = viewportWidth <= 768;
      const tabletViewport = viewportWidth <= 1100;
      const width = mobileViewport ? Math.min(window.innerWidth - 24, 320) : tabletViewport ? 280 : 300;
      const right = 12;
      const top = Math.max(12, Math.min(rect.bottom + 12, window.innerHeight - 24));

      setProfileMenuPosition({ top, right, width });
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowProfileMenu(false);
      }
    };

    updateProfileMenuPosition();
    window.addEventListener("resize", updateProfileMenuPosition);
    window.addEventListener("scroll", updateProfileMenuPosition, true);
    document.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("resize", updateProfileMenuPosition);
      window.removeEventListener("scroll", updateProfileMenuPosition, true);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showProfileMenu, viewportWidth]);

  const handleLogout = () => {
    localStorage.removeItem("franchise");
    localStorage.removeItem("franchiseLastActiveAt");
    navigate("/franchise-login");
  };

  const handleProfileEdit = () => {
    setShowProfileMenu(false);
    navigate("/franchise-dashboard/settings/profile");
  };

  const handleChangePassword = () => {
    setShowProfileMenu(false);
    navigate("/franchise-dashboard/settings/profile");
  };

  const avatarText = (franchise?.centre_name || franchise?.login_id || "FR")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const menuItems = useMemo(
    () => [
      {
        icon: "bi-house",
        label: "Home",
        path: "/franchise-dashboard",
      },
      {
        icon: "bi-book",
        label: "Course",
        children: [
          { label: "View Course", path: "/franchise-dashboard/course/view" },
          { label: "View Course Fees", path: "/franchise-dashboard/course/fees" },
        ],
      },
      {
        icon: "bi-people",
        label: "Student",
        children: [
          { label: "New Application", path: "/franchise-dashboard/student/new-application" },
          { label: "Applied Student", path: "/franchise-dashboard/student/applied-student" },
          { label: "Accepted Student", path: "/franchise-dashboard/student/accepted-student" },
          { label: "Registered Student", path: "/franchise-dashboard/student/registered-student" },
          { label: "Exam Completed Student", path: "/franchise-dashboard/student/exam-completed-student" },
          { label: "Certified Student", path: "/franchise-dashboard/student/certified-student" },
          { label: "Our Student", path: "/franchise-dashboard/student/our-student" },
        ],
      },
      {
        icon: "bi-play-btn",
        label: "Training",
        children: [{ label: "Franchise Training", path: "/franchise-dashboard/training/videos" }],
      },
      {
        icon: "bi-inboxes",
        label: "Front Office",
        children: [
          { label: "Postal Order", path: "/franchise-dashboard/front-office/postal-order" },
          { label: "Postal Dispatch", path: "/franchise-dashboard/front-office/postal-dispatch" },
        ],
      },
      {
        icon: "bi-wallet2",
        label: "Payment",
        children: [
          { label: "My Wallet", path: "/franchise-dashboard/payment/wallet" },
          { label: "Load Wallet", path: "/franchise-dashboard/payment/load-money" },
        ],
      },
      {
        icon: "bi-receipt",
        label: "Account Info",
        children: [{ label: "View Statement", path: "/franchise-dashboard/account/statement" }],
      },
      {
        icon: "bi-file-earmark-text",
        label: "Exam",
        children: [
          { label: "Exam Create & List", path: "/franchise-dashboard/exam/list" },
          { label: "MCQ Question & CSV Upload", path: "/franchise-dashboard/exam/questions" },
          { label: "Online Exam Result", path: "/franchise-dashboard/exam/results" },
        ],
      },
      {
        icon: "bi-award",
        label: "Certification",
        children: [
          { label: "Marksheet Download", path: "/franchise-dashboard/certification/marksheet" },
          { label: "Certificate Download", path: "/franchise-dashboard/certification/certificate" },
        ],
      },
      {
        icon: "bi-gear",
        label: "Settings",
        children: [{ label: "Profile Setting", path: "/franchise-dashboard/settings/profile" }],
      },
      {
        icon: "bi-chat-dots",
        label: "Message",
        children: [
          { label: "New Message", path: "/franchise-dashboard/message/new" },
          { label: "Ticket Box", path: "/franchise-dashboard/message/ticket-box" },
        ],
      },
    ],
    []
  );

  const isMainActive = (item) =>
    item.path
      ? location.pathname === item.path
      : item.children?.some((sub) => location.pathname === sub.path);

  const pageTitles = {
    "/franchise-dashboard": "Dashboard",
    "/franchise-dashboard/course/view": "View Course",
    "/franchise-dashboard/course/fees": "View Course Fees",
    "/franchise-dashboard/student/new-application": "New Application",
    "/franchise-dashboard/student/applied-student": "Applied Student",
    "/franchise-dashboard/student/accepted-student": "Accepted Student",
    "/franchise-dashboard/student/registered-student": "Registered Student",
    "/franchise-dashboard/student/exam-completed-student": "Exam Completed Student",
    "/franchise-dashboard/student/certified-student": "Certified Student",
    "/franchise-dashboard/student/our-student": "Our Student",
    "/franchise-dashboard/student/view-application": "Applied Student",
    "/franchise-dashboard/training/videos": "Franchise Training",
    "/franchise-dashboard/front-office/postal-order": "Postal Order",
    "/franchise-dashboard/front-office/postal-dispatch": "Postal Dispatch",
    "/franchise-dashboard/payment/wallet": "My Wallet",
    "/franchise-dashboard/payment/load-money": "Load Wallet",
    "/franchise-dashboard/account/statement": "View Statement",
    "/franchise-dashboard/exam/list": "Exam Create and View",
    "/franchise-dashboard/exam/questions": "MCQ Question and CSV Upload",
    "/franchise-dashboard/exam/results": "Online Exam Result",
    "/franchise-dashboard/certification/marksheet": "Marksheet Download",
    "/franchise-dashboard/certification/certificate": "Certificate Download",
    "/franchise-dashboard/settings/profile": "Profile Setting",
    "/franchise-dashboard/message/new": "New Message",
    "/franchise-dashboard/message/view": "View Message",
    "/franchise-dashboard/message/request": "Message Request",
    "/franchise-dashboard/message/ticket-box": "Ticket Box",
  };

  const isMobile = viewportWidth <= 768;
  const isTablet = viewportWidth <= 1100;

  const responsivePageStyle = {
    ...pageStyle,
    padding: isMobile ? "0 0 16px 0" : pageStyle.padding,
    overflowX: "hidden",
  };

  const responsiveAppShellStyle = {
    ...appShellStyle,
    gridTemplateColumns: isTablet ? "1fr" : appShellStyle.gridTemplateColumns,
  };

  const responsiveSidebarStyle = {
    ...sidebarStyle,
    minHeight: "100vh",
    padding: isMobile ? "16px 14px" : isTablet ? "18px 16px" : sidebarStyle.padding,
    position: isMobile ? "fixed" : "relative",
    top: isMobile ? 0 : "auto",
    left: isMobile ? 0 : "auto",
    width: isMobile ? "min(86vw, 320px)" : "248px",
    height: "100vh",
    zIndex: isMobile ? 40 : "auto",
    transform: isMobile ? `translateX(${showSidebarMenu ? "0" : "-105%"})` : "none",
    transition: isMobile ? "transform 0.25s ease" : "none",
    overflowY: "auto",
    overflowX: "hidden",
    alignSelf: "start",
  };

  const responsiveMainPanelStyle = {
    ...mainPanelStyle,
    minHeight: isTablet ? "auto" : mainPanelStyle.minHeight,
  };

  const responsiveTopBarShellStyle = {
    ...topBarShellStyle,
    padding: isMobile ? "14px" : isTablet ? "16px" : topBarShellStyle.padding,
  };

  const responsiveTopBarStyle = {
    ...topBarStyle,
    alignItems: isMobile ? "flex-start" : topBarStyle.alignItems,
    flexDirection: isMobile ? "column" : "row",
  };

  const mobileTopRowStyle = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  };

  const responsiveContentWrapStyle = {
    ...contentWrapStyle,
    padding: isMobile ? "14px" : isTablet ? "18px" : contentWrapStyle.padding,
  };

  const responsiveContentCardStyle = {
    ...contentCardStyle,
    borderRadius: isMobile ? "18px" : contentCardStyle.borderRadius,
    padding: isMobile ? "16px" : isTablet ? "20px" : contentCardStyle.padding,
  };

  const sidebarFooterStyle = {
    marginTop: "auto",
    paddingTop: "18px",
    borderTop: "1px solid rgba(148, 163, 184, 0.16)",
    color: "#7c2d3a",
    fontSize: isMobile ? "11px" : "12px",
    lineHeight: 1.6,
  };

  const headingStyle = {
    margin: isMobile ? "2px 0 6px" : "5px 0 6px",
    fontSize: isMobile ? "22px" : isTablet ? "24px" : "28px",
    color: "#4e1b24",
    fontWeight: 800,
    lineHeight: 1.2,
  };

  const pagePillStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 12px",
    borderRadius: "999px",
    background: "rgba(255,255,255,0.72)",
    border: "1px solid rgba(148, 163, 184, 0.22)",
    fontSize: "12px",
    maxWidth: "100%",
    color: "#7c2d3a",
  };

  const topActionsStyle = {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    alignItems: "center",
    position: "relative",
    paddingRight: isMobile ? "0" : "18px",
    width: isMobile ? "100%" : "auto",
    justifyContent: isMobile ? "space-between" : "flex-start",
  };

  const actionButtonSizeStyle = {
    width: isMobile ? "42px" : "46px",
    height: isMobile ? "42px" : "46px",
  };

  const hamburgerButtonStyle = {
    ...primaryButtonStyle,
    ...actionButtonSizeStyle,
    flexShrink: 0,
  };

  const responsiveProfileMenuStyle = {
    ...profileMenuStyle,
    top: `${profileMenuPosition.top}px`,
    right: `${profileMenuPosition.right}px`,
    width: `${profileMenuPosition.width}px`,
    maxWidth: "calc(100vw - 24px)",
    maxHeight: "calc(100vh - 24px)",
    overflowY: "auto",
  };

  const mobileOverlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(78, 27, 36, 0.22)",
    backdropFilter: "blur(4px)",
    zIndex: 30,
  };

  const headlineCards = [
    {
      label: "Students",
      value: summary.application_count || 0,
      icon: "bi-people-fill",
      helper: "Applications created from this franchise",
    },
    {
      label: "Courses",
      value: summary.course_count || 0,
      icon: "bi-journal-richtext",
      helper: "Available courses visible in portal",
    },
    {
      label: "Exams",
      value: summary.exam_count || 0,
      icon: "bi-patch-check-fill",
      helper: "Exam rows attached to this account",
    },
    {
      label: "Messages",
      value: summary.message_count || 0,
      icon: "bi-chat-dots-fill",
      helper: "Message activity linked with this franchise",
    },
  ];

  useEffect(() => {
    if (!isMobile && showSidebarMenu) {
      setShowSidebarMenu(false);
    }
  }, [isMobile, showSidebarMenu]);

  useEffect(() => {
    if (isMobile) {
      setShowSidebarMenu(false);
      setShowProfileMenu(false);
    }
  }, [location.pathname, isMobile]);

  return (
    <div style={responsivePageStyle}>
      <style>
        {`
          .franchise-sidebar {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .franchise-sidebar::-webkit-scrollbar {
            display: none;
            width: 0;
            height: 0;
          }
        `}
      </style>
      <div style={responsiveAppShellStyle}>
        {isMobile && showSidebarMenu ? <div style={mobileOverlayStyle} onClick={() => setShowSidebarMenu(false)} /> : null}

        <aside className="franchise-sidebar" style={responsiveSidebarStyle}>
          <div>
            <div
              style={{
                background: "linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(255,237,213,0.88) 100%)",
                border: "1px solid rgba(148, 163, 184, 0.16)",
                borderRadius: "22px",
                padding: isMobile ? "14px 12px" : "16px 14px",
                textAlign: "center",
                boxShadow: "0 18px 34px rgba(148, 163, 184, 0.14)",
                marginBottom: "18px",
              }}
            >
              <img src={logo} alt="NIDCS" style={{ width: "78%", maxWidth: "150px" }} />
              <div style={{ marginTop: "12px" }}>
                <p style={{ margin: 0, fontSize: "11px", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(78, 27, 36, 0.64)" }}>
                  Franchise Portal
                </p>
                <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#7c2d3a", fontWeight: 700 }}>
                  {franchise?.centre_code || franchise?.login_id || "No code"}
                </p>
              </div>
            </div>

            <div
              style={{
                marginBottom: "4px",
                textAlign: "center",
                color: "#7c2d3a",
              }}
            >
              <div style={{ fontSize: "11px", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(78, 27, 36, 0.62)", fontWeight: 700 }}>
                Student List Session
              </div>
              <div style={{ marginTop: "8px" }}>
                <SessionDropdown
                  value={selectedSession}
                  options={availableSessions.length ? availableSessions : [activeSession]}
                  onChange={setSelectedSession}
                />
              </div>
              <div style={{ marginTop: "8px", fontSize: "11px", color: "rgba(78, 27, 36, 0.68)", lineHeight: 1.5 }}>
                New admission will save only in active session {activeSession}.
              </div>
            </div>

            <div style={{ marginTop: "12px", padding: "0 10px", borderTop: "1px solid rgba(148, 163, 184, 0.16)", paddingTop: "12px" }}>
              {menuItems.map((item, index) => {
                const itemActive = isMainActive(item);

                return (
                  <div key={index}>
                    <div
                      onClick={() => {
                        if (item.children) {
                          setOpenMenu(openMenu === index ? null : index);
                        } else {
                          navigate(item.path);
                          if (isMobile) setShowSidebarMenu(false);
                        }
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 14px",
                        cursor: "pointer",
                        margin: "6px 0",
                        borderRadius: "16px",
                        transition: "0.2s ease",
                        background: itemActive ? "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)" : "transparent",
                        border: itemActive ? "1px solid rgba(124, 45, 58, 0.18)" : "1px solid transparent",
                        boxShadow: itemActive ? "0 16px 28px rgba(124, 45, 58, 0.20)" : "none",
                      }}
                      onMouseEnter={(e) => {
                        if (!itemActive) {
                          e.currentTarget.style.background = "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)";
                          e.currentTarget.style.boxShadow = "0 16px 28px rgba(124, 45, 58, 0.20)";
                          e.currentTarget.style.transform = "translateX(4px)";
                          e.currentTarget.style.border = "1px solid rgba(124, 45, 58, 0.12)";
                          const icon = e.currentTarget.querySelector(".franchise-sidebar-main-icon");
                          const label = e.currentTarget.querySelector(".franchise-sidebar-main-label");
                          const arrow = e.currentTarget.querySelector(".franchise-sidebar-main-arrow");
                          if (icon) icon.style.color = "#ffffff";
                          if (label) label.style.color = "#ffffff";
                          if (arrow) arrow.style.color = "rgba(255,255,255,0.84)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!itemActive) {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.boxShadow = "none";
                          e.currentTarget.style.transform = "translateX(0)";
                          e.currentTarget.style.border = "1px solid transparent";
                          const icon = e.currentTarget.querySelector(".franchise-sidebar-main-icon");
                          const label = e.currentTarget.querySelector(".franchise-sidebar-main-label");
                          const arrow = e.currentTarget.querySelector(".franchise-sidebar-main-arrow");
                          if (icon) icon.style.color = "#7c2d3a";
                          if (label) label.style.color = "#4e1b24";
                          if (arrow) arrow.style.color = "rgba(78, 27, 36, 0.58)";
                        }
                      }}
                    >
                      <div style={{ display: "flex", gap: "12px", alignItems: "center", minWidth: 0 }}>
                        <div
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "12px",
                            background: itemActive ? "rgba(255,255,255,0.18)" : "rgba(124, 45, 58, 0.08)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <i className={`bi ${item.icon} franchise-sidebar-main-icon`} style={{ fontSize: "16px", color: itemActive ? "#ffffff" : "#7c2d3a" }}></i>
                        </div>

                        <span className="franchise-sidebar-main-label" style={{ fontSize: "14px", fontWeight: 600, color: itemActive ? "#ffffff" : "#4e1b24", whiteSpace: "nowrap" }}>
                          {item.label}
                        </span>
                      </div>

                      {item.children && (
                        <i
                          className={`bi ${openMenu === index ? "bi-chevron-up" : "bi-chevron-down"} franchise-sidebar-main-arrow`}
                          style={{ color: itemActive ? "rgba(255,255,255,0.84)" : "rgba(78, 27, 36, 0.58)", fontSize: "13px" }}
                        ></i>
                      )}
                    </div>

                    {openMenu === index && item.children && (
                      <div
                        style={{
                          margin: "4px 0 8px 16px",
                          paddingLeft: "16px",
                          borderLeft: "1px solid rgba(124, 45, 58, 0.14)",
                        }}
                      >
                        {item.children.map((sub, i) => {
                          const subActive = location.pathname === sub.path;

                          return (
                            <div
                              key={i}
                              onClick={() => {
                                navigate(sub.path);
                                if (isMobile) setShowSidebarMenu(false);
                              }}
                              style={{
                                padding: "10px 12px",
                                cursor: "pointer",
                                fontSize: "13px",
                                borderRadius: "12px",
                                transition: "0.2s ease",
                                background: subActive ? "rgba(124, 45, 58, 0.10)" : "transparent",
                                color: subActive ? "#7c2d3a" : "#6b3140",
                                marginBottom: "4px",
                                border: subActive ? "1px solid rgba(124, 45, 58, 0.14)" : "1px solid transparent",
                              }}
                              onMouseEnter={(e) => {
                                if (!subActive) {
                                  e.currentTarget.style.background = "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)";
                                  e.currentTarget.style.paddingLeft = "16px";
                                  e.currentTarget.style.boxShadow = "0 12px 20px rgba(124, 45, 58, 0.14)";
                                  e.currentTarget.style.border = "1px solid rgba(124, 45, 58, 0.12)";
                                  e.currentTarget.style.color = "#ffffff";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!subActive) {
                                  e.currentTarget.style.background = "transparent";
                                  e.currentTarget.style.paddingLeft = "12px";
                                  e.currentTarget.style.boxShadow = "none";
                                  e.currentTarget.style.border = "1px solid transparent";
                                  e.currentTarget.style.color = "#6b3140";
                                }
                              }}
                            >
                              <span style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 500 }}>
                                <i className="bi bi-chevron-right" style={{ fontSize: "11px" }}></i>
                                {sub.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div style={sidebarFooterStyle}>
            <div>Franchise Portal Dashboard</div>
            <div>{franchise?.centre_name || franchise?.login_id || "Franchise User"} Workspace</div>
          </div>
        </aside>

        <main style={responsiveMainPanelStyle}>
          <div style={responsiveTopBarShellStyle}>
            <div style={responsiveTopBarStyle}>
              <div style={{ minWidth: 0, width: isMobile ? "100%" : "auto" }}>
                <div style={mobileTopRowStyle}>
                  {isMobile ? (
                    <button type="button" style={hamburgerButtonStyle} onClick={() => setShowSidebarMenu((prev) => !prev)} aria-label="Toggle sidebar menu">
                      <i className={`bi ${showSidebarMenu ? "bi-x-lg" : "bi-list"}`}></i>
                    </button>
                  ) : null}

                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h1 style={headingStyle}>
                      Welcome, {franchise?.centre_name || franchise?.login_id || "Franchise User"}
                    </h1>
                    <div style={pagePillStyle}>
                      <i className="bi bi-stars"></i>
                      {pageTitles[location.pathname] || "Franchise Workspace"}
                    </div>
                  </div>
                </div>
              </div>

              <div style={topActionsStyle} ref={profileMenuRef}>
                <NotificationBell actorType="franchise" actorId={franchise?.id || 0} actorName={franchise?.centre_name || franchise?.login_id || "Franchise"} />
                <button
                  type="button"
                  ref={profileButtonRef}
                  style={{ ...avatarButtonStyle, ...actionButtonSizeStyle }}
                  onClick={() => setShowProfileMenu((prev) => !prev)}
                  aria-expanded={showProfileMenu}
                  aria-label="Open profile menu"
                >
                  {profilePhoto ? <img src={profilePhoto} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} /> : avatarText}
                </button>

                {showProfileMenu && (
                  <div style={responsiveProfileMenuStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "14px" }}>
                      <div
                        style={{
                          width: "58px",
                          height: "58px",
                          borderRadius: "18px",
                          background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: "20px",
                        }}
                      >
                        {profilePhoto ? <img src={profilePhoto} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} /> : avatarText}
                      </div>
                      <div>
                        <h3 style={{ margin: 0, color: "#0f172a", fontSize: "18px" }}>{franchise?.centre_name || "Franchise User"}</h3>
                        <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "14px", overflowWrap: "anywhere", wordBreak: "break-word" }}>{franchise?.login_id || "No login id"}</p>
                      </div>
                    </div>

                    <div style={{ ...profileActionStyle, background: "#fff7ed", border: "1px solid rgba(194, 65, 12, 0.14)", color: "#7c2d3a" }}>
                      <i className="bi bi-image" style={{ marginRight: "8px" }}></i>
                      Profile Picture Ready for Edit
                    </div>
                    <button type="button" style={profileActionStyle} onClick={handleProfileEdit}>
                      <i className="bi bi-pencil-square" style={{ marginRight: "8px" }}></i>
                      Edit Profile
                    </button>
                    <button type="button" style={profileActionStyle} onClick={handleChangePassword}>
                      <i className="bi bi-shield-lock" style={{ marginRight: "8px" }}></i>
                      Change Password
                    </button>
                    <button type="button" style={{ ...profileActionStyle, background: "#fef2f2", color: "#b91c1c" }} onClick={handleLogout}>
                      <i className="bi bi-box-arrow-right" style={{ marginRight: "8px" }}></i>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={responsiveContentWrapStyle}>
            {location.pathname === "/franchise-dashboard" && (
              <>
                <section
                  style={{
                    ...franchiseHeroStyle,
                    padding: isMobile ? "22px 18px" : franchiseHeroStyle.padding,
                  }}
                >
                  <div style={franchiseHeroGlowStyle} />
                  <div style={{ position: "relative", zIndex: 1, display: "grid", gap: "20px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "18px",
                        flexWrap: "wrap",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ flex: "1 1 620px", maxWidth: "720px", minWidth: 0 }}>
                        <p
                          style={{
                            margin: 0,
                            opacity: 0.78,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            fontSize: "12px",
                            fontWeight: 700,
                          }}
                        >
                          Franchise Control Room
                        </p>
                        <h2
                          style={{
                            margin: "8px 0 12px",
                            fontSize: isMobile ? "28px" : "34px",
                            lineHeight: 1.1,
                          }}
                        >
                          Franchise dashboard with live wallet and operation snapshot
                        </h2>
                        <p
                          style={{
                            margin: 0,
                            maxWidth: "640px",
                            color: "rgba(255,255,255,0.82)",
                            lineHeight: 1.75,
                          }}
                        >
                          Student admission, wallet balance, exams, certification downloads,
                          and active session visibility are organized here in one clean
                          franchise workspace.
                        </p>
                      </div>

                      <div
                        style={{
                          width: "280px",
                          maxWidth: "100%",
                          display: "grid",
                          gap: "10px",
                          alignSelf: "flex-start",
                          flexShrink: 0,
                          marginLeft: "auto",
                        }}
                      >
                        <div style={{ ...franchiseHeroSideCardStyle, padding: "14px" }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: "10px",
                              alignItems: "center",
                              marginBottom: "10px",
                            }}
                          >
                            <div
                              style={{
                                color: "rgba(255,255,255,0.76)",
                                fontSize: "11px",
                                fontWeight: 800,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                              }}
                            >
                              Centre Summary
                            </div>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "5px 9px",
                                borderRadius: "999px",
                                background: "rgba(255,255,255,0.16)",
                                fontSize: "11px",
                                fontWeight: 700,
                                color: "#fff7ed",
                              }}
                            >
                              <i className="bi bi-stars"></i>
                              Active
                            </span>
                          </div>
                          <div style={{ display: "grid", gap: "8px", color: "#ffffff" }}>
                            <div style={{ fontSize: "14px", fontWeight: 800, lineHeight: 1.4 }}>
                              {franchise?.centre_name || franchise?.login_id || "Franchise User"}
                            </div>
                            <div style={{ color: "rgba(255,255,255,0.82)", fontSize: "12px" }}>
                              Code: {franchise?.centre_code || franchise?.login_id || "Not available"}
                            </div>
                            <div style={{ color: "rgba(255,255,255,0.82)", fontSize: "12px" }}>
                              Admission Session: {activeSession}
                            </div>
                            <div style={{ marginTop: "10px" }}>
                              <div style={{ color: "rgba(255,255,255,0.68)", fontSize: "11px", fontWeight: 700, marginBottom: "6px" }}>
                                Student List Session
                              </div>
                              <SessionDropdown
                                value={selectedSession}
                                options={availableSessions.length ? availableSessions : [activeSession]}
                                onChange={setSelectedSession}
                                variant="hero"
                              />
                            </div>
                          </div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px" }}>
                          <div style={{ ...franchiseHeroSideCardStyle, padding: "13px 14px" }}>
                            <div style={{ color: "rgba(255,255,255,0.68)", fontSize: "11px", fontWeight: 700 }}>
                              Wallet Balance
                            </div>
                            <div style={{ fontSize: "18px", fontWeight: 800, marginTop: "8px", lineHeight: 1.4, color: "#ffffff" }}>
                              Rs {animatedWalletBalance.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0, 1fr))",
                        gap: "12px",
                      }}
                    >
                      {headlineCards.map((card) => (
                        <div key={card.label} style={franchiseHeroSideCardStyle}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start" }}>
                            <div>
                              <div style={{ color: "rgba(255,255,255,0.76)", fontSize: "12px", fontWeight: 700 }}>
                                {card.label}
                              </div>
                              <div style={{ marginTop: "8px", fontSize: "28px", fontWeight: 800, color: "#ffffff" }}>
                                {card.value}
                              </div>
                              <div style={{ marginTop: "4px", color: "rgba(255,255,255,0.74)", fontSize: "12px", lineHeight: 1.5 }}>
                                {card.helper}
                              </div>
                            </div>
                            <div
                              style={{
                                width: "42px",
                                height: "42px",
                                borderRadius: "14px",
                                background: "rgba(255,255,255,0.16)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "18px",
                                color: "#ffffff",
                                flexShrink: 0,
                              }}
                            >
                              <i className={`bi ${card.icon}`}></i>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={() => navigate("/franchise-dashboard/student/new-application")}
                      >
                        New Student
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-light"
                        onClick={() => navigate("/franchise-dashboard/payment/load-money")}
                      >
                        Load Wallet
                      </button>
                    </div>
                  </div>
                </section>

                <section
                  style={{
                    background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 52%, #c2410c 100%)",
                    color: "#fff",
                    borderRadius: "28px",
                    padding: isMobile ? "22px 18px" : "28px",
                    boxShadow: "0 30px 60px rgba(78, 27, 36, 0.24)",
                    position: "relative",
                    overflow: "hidden",
                    marginBottom: "20px",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      inset: "auto -60px -90px auto",
                      width: "230px",
                      height: "230px",
                      background: "radial-gradient(circle, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 70%)",
                    }}
                  />

                  <div
                style={{
                  position: "relative",
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
                  gap: "18px",
                  alignItems: "start",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0, 1fr))",
                    gap: "12px",
                    gridColumn: "1 / -1",
                  }}
                >
                  {(() => {
                    const toolsPoint = Number((summary.tools_point_balance ?? ((summary.wallets || []).find((wallet) => wallet.key === "tools_wallet")?.balance)) || 0);
                    const giftPoint = Number((summary.gift_card_point_balance ?? ((summary.wallets || []).find((wallet) => wallet.key === "gift_card_wallet")?.balance)) || 0);
                    const skillsPoint = Number((summary.skills_point_balance ?? ((summary.wallets || []).find((wallet) => wallet.key === "reserve_wallet")?.balance)) || 0);
                    const totalPoint = Number((summary.total_point_balance ?? (toolsPoint + giftPoint + skillsPoint)) || 0);

                    return [
                    {
                      key: "tools_point",
                      label: "Tools Point",
                      balance: toolsPoint,
                    },
                    {
                      key: "gift_card_wallet",
                      label: "Gift Card",
                      balance: giftPoint,
                    },
                    {
                      key: "skills_point",
                      label: "Skills Point",
                      balance: skillsPoint,
                    },
                    {
                      key: "total_point",
                      label: "Total Point",
                      balance: totalPoint,
                    },
                  ].map((wallet) => (
                    <div
                      key={wallet.key}
                      style={{
                        background: "rgba(255,255,255,0.14)",
                        border: "1px solid rgba(255,255,255,0.18)",
                        borderRadius: "18px",
                        padding: "14px 16px",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
                      }}
                    >
                      <strong style={{ display: "block", marginBottom: "10px" }}>{wallet.label}</strong>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                        <span>
                          {Number(wallet.balance || 0).toLocaleString("en-IN")}
                        </span>
                        <span
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "999px",
                            background: "rgba(255,255,255,0.18)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            flexShrink: 0,
                          }}
                        >
                          <i className="bi bi-coin"></i>
                        </span>
                      </div>
                    </div>
                  ));
                  })()}
                </div>
                  </div>
                </section>
              </>
            )}

            <section style={responsiveContentCardStyle}>
              <Outlet context={{ franchise, summary, activeSession, selectedSession, availableSessions }} />
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

export default FranchiseDashboard;
